import { useEffect, useMemo, useState } from 'react'

import { dropdownApi } from '../../dropdown/services/dropdownApi'
import type { DropdownOption } from '../../dropdown/types/dropdown.types'
import { publisherApi } from '../services/publisherApi'
import type {
  CreatePublisherFormErrors,
  CreatePublisherInput,
  ManagedUser,
  PublisherFilters,
  UpdatePublisherInput,
} from '../types/publisher.types'
import type { ApiError, SnackbarState } from '../../../shared/types/common'

const EMPTY_CREATE_FORM: CreatePublisherInput = {
  name: '',
  email: '',
  password: '',
  role: 'author',
  discipline: '',
}

const EMPTY_EDIT_FORM: UpdatePublisherInput = {
  name: '',
  email: '',
  role: 'author',
  discipline: '',
  is_active: true,
  password: '',
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const NAME_PATTERN = /^[A-Za-zÀ-ÿ' -]+$/
const PASSWORD_RULE_MESSAGE =
  'Password must be at least 12 characters and include uppercase, lowercase, number, and special character.'

function validateCreatePublisherForm(
  form: CreatePublisherInput,
): CreatePublisherFormErrors {
  const errors: CreatePublisherFormErrors = {}

  if (!form.name.trim()) {
    errors.name = 'Full name is required'
  } else if (!NAME_PATTERN.test(form.name.trim())) {
    errors.name = 'Full name cannot contain numbers'
  }

  if (!form.email.trim()) {
    errors.email = 'Email is required'
  } else if (!EMAIL_PATTERN.test(form.email.trim())) {
    errors.email = 'Enter a valid email address'
  }

  if (!form.password) {
    errors.password = 'Password is required'
  } else {
    const isStrongPassword =
      form.password.length >= 12 &&
      /[A-Z]/.test(form.password) &&
      /[a-z]/.test(form.password) &&
      /\d/.test(form.password) &&
      /[^A-Za-z0-9]/.test(form.password)

    if (!isStrongPassword) {
      errors.password = PASSWORD_RULE_MESSAGE
    }
  }

  return errors
}

function validateCreatePublisherField(
  field: keyof CreatePublisherInput,
  form: CreatePublisherInput,
) {
  return validateCreatePublisherForm(form)[field]
}

export function usePublisher() {
  const [publishers, setPublishers] = useState<ManagedUser[]>([])
  const [filters, setFilters] = useState<PublisherFilters>({ search: '', role: 'all' })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [disciplineOptions, setDisciplineOptions] = useState<DropdownOption[]>([])
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [createForm, setCreateForm] = useState<CreatePublisherInput>(EMPTY_CREATE_FORM)
  const [createFormErrors, setCreateFormErrors] = useState<CreatePublisherFormErrors>({})
  const [editingPublisher, setEditingPublisher] = useState<ManagedUser | null>(null)
  const [editForm, setEditForm] = useState<UpdatePublisherInput>(EMPTY_EDIT_FORM)
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  })

  useEffect(() => {
    void loadPublishers()
    void loadDisciplineOptions()
  }, [])

  const filteredPublishers = useMemo(() => {
    const search = filters.search?.trim().toLowerCase()
    return publishers.filter((publisher) => {
      const matchesRole = !filters.role || filters.role === 'all' || publisher.role === filters.role
      const matchesSearch =
        !search ||
        [publisher.name, publisher.email, publisher.role, publisher.discipline || '', publisher.is_active ? 'active' : 'inactive'].some((value) =>
          value.toLowerCase().includes(search),
        )

      return matchesRole && matchesSearch
    })
  }, [filters.role, filters.search, publishers])

  const isCreateFormValid = useMemo(
    () => Object.keys(validateCreatePublisherForm(createForm)).length === 0,
    [createForm],
  )

  async function loadPublishers() {
    setLoading(true)
    try {
      const response = await publisherApi.list()
      setPublishers(response.data)
    } catch {
      setSnackbar({
        open: true,
        message: 'Failed to load users',
        severity: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  async function loadDisciplineOptions() {
    try {
      const response = await dropdownApi.getByField('discipline')
      setDisciplineOptions(response.data)
    } catch {
      setSnackbar({
        open: true,
        message: 'Failed to load discipline options',
        severity: 'error',
      })
    }
  }

  async function createPublisher() {
    const validationErrors = validateCreatePublisherForm(createForm)
    setCreateFormErrors(validationErrors)

    if (Object.keys(validationErrors).length > 0) {
      setSnackbar({
        open: true,
        message: 'Please fix the user form errors',
        severity: 'error',
      })
      return
    }

    setSaving(true)
    try {
      const response = await publisherApi.create(createForm)
      setPublishers((current) =>
        [...current, response.data].sort((left, right) =>
          left.name.localeCompare(right.name),
        ),
      )
      setCreateForm(EMPTY_CREATE_FORM)
      setCreateFormErrors({})
      setCreateDialogOpen(false)
      setSnackbar({
        open: true,
        message: 'User created',
        severity: 'success',
      })
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          (error as ApiError).response?.data?.message ||
          'Failed to create user',
        severity: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  function openEditPublisher(publisher: ManagedUser) {
    setEditingPublisher(publisher)
    setEditForm({
      name: publisher.name,
      email: publisher.email,
      role: publisher.role,
      discipline: publisher.discipline || '',
      is_active: publisher.is_active,
      password: '',
    })
  }

  function closeEditPublisher() {
    setEditingPublisher(null)
    setEditForm(EMPTY_EDIT_FORM)
  }

  function openCreatePublisher() {
    setCreateDialogOpen(true)
  }

  function closeCreatePublisher() {
    setCreateDialogOpen(false)
    setCreateForm(EMPTY_CREATE_FORM)
    setCreateFormErrors({})
  }

  function updateCreateFormField(
    field: keyof CreatePublisherInput,
    value: string,
  ) {
    setCreateForm((current) => {
      const nextForm = { ...current, [field]: value }
      setCreateFormErrors((currentErrors) => ({
        ...currentErrors,
        [field]: validateCreatePublisherField(field, nextForm),
      }))
      return nextForm
    })
  }

  async function updatePublisher() {
    if (!editingPublisher) {
      return
    }

    setSaving(true)
    try {
      const payload: UpdatePublisherInput = {
        name: editForm.name,
        email: editForm.email,
        role: editForm.role,
        discipline: editForm.role === 'publisher' ? editForm.discipline : '',
        is_active: editForm.is_active,
        ...(editForm.password?.trim() ? { password: editForm.password } : {}),
      }

      const response = await publisherApi.update(editingPublisher.id, payload)
      setPublishers((current) =>
        current
          .map((publisher) =>
            publisher.id === editingPublisher.id ? response.data : publisher,
          )
          .sort((left, right) => left.name.localeCompare(right.name)),
      )
      closeEditPublisher()
      setSnackbar({
        open: true,
        message: 'User updated',
        severity: 'success',
      })
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          (error as ApiError).response?.data?.message ||
          'Failed to update user',
        severity: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  async function deletePublisher(publisher: ManagedUser) {
    setSaving(true)
    try {
      await publisherApi.remove(publisher.id)
      setPublishers((current) =>
        current.filter((currentPublisher) => currentPublisher.id !== publisher.id),
      )
      setSnackbar({
        open: true,
        message: 'User deleted',
        severity: 'success',
      })
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          (error as ApiError).response?.data?.message ||
          'Failed to delete user',
        severity: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  return {
    closeCreatePublisher,
    closeEditPublisher,
    createDialogOpen,
    createForm,
    createFormErrors,
    createPublisher,
    deletePublisher,
    disciplineOptions,
    editForm,
    editingPublisher,
    filteredPublishers,
    filters,
    loading,
    openCreatePublisher,
    openEditPublisher,
    saving,
    isCreateFormValid,
    updateCreateFormField,
    setCreateForm,
    setCreateFormErrors,
    setEditForm,
    setFilters,
    setSnackbar,
    snackbar,
    updatePublisher,
  }
}
